import React, { useState, useRef, useEffect } from 'react';
import { usePageEditMode } from '@/context/PageEditModeContext';
import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';

/**
 * Editable button/link. Reads `path.label` and `path.href` from the page
 * config. When in edit mode, hovering the link reveals a small pencil
 * button — clicking it opens a floating popover to edit both the label
 * text and the destination URL. Otherwise renders a normal <Link> (for
 * internal /paths) or <a> (for absolute URLs).
 *
 * Props:
 *   path         dotted config path (values stored under `${path}.label` and `${path}.href`)
 *   fallbackLabel default label
 *   fallbackHref  default destination
 *   className     passthrough
 *   children      icons/etc rendered inside the button alongside the label
 */
export const EditableLink = ({
  path,
  fallbackLabel = '',
  fallbackHref = '#',
  className = '',
  children,
  ...rest
}) => {
  const { enabled, getValue, update } = usePageEditMode();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [href, setHref] = useState('');
  const popRef = useRef(null);

  const savedLabel = getValue(`${path}.label`);
  const savedHref = getValue(`${path}.href`);
  const displayLabel = savedLabel === undefined || savedLabel === null || savedLabel === ''
    ? fallbackLabel : savedLabel;
  const displayHref = savedHref === undefined || savedHref === null || savedHref === ''
    ? fallbackHref : savedHref;

  useEffect(() => {
    if (!open) return undefined;
    setLabel(displayLabel);
    setHref(displayHref);
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = () => {
    update(`${path}.label`, label);
    update(`${path}.href`, href);
    setOpen(false);
  };

  const isExternal = /^https?:\/\//i.test(displayHref);
  const Comp = isExternal ? 'a' : Link;
  const linkProps = isExternal ? { href: displayHref, rel: 'noopener' } : { to: displayHref };

  if (!enabled) {
    return (
      <Comp className={className} {...linkProps} {...rest}>
        {children}
        {displayLabel}
      </Comp>
    );
  }

  return (
    <span className="relative inline-flex" data-edit-safe>
      <span className={`${className} outline-dashed outline-2 outline-yellow-400/70 outline-offset-2 rounded-sm`}>
        {children}
        {displayLabel}
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="ml-1 self-center p-1 rounded-full bg-yellow-400 text-black hover:bg-yellow-300"
        data-edit-safe
        title="Edit link"
      >
        <Pencil className="w-3 h-3" />
      </button>
      {open && (
        <div
          ref={popRef}
          data-edit-safe
          className="absolute z-[10000] top-full mt-2 left-0 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl p-3 w-72"
        >
          <label className="block text-xs text-zinc-300 mb-1">Label</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full mb-2 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
          />
          <label className="block text-xs text-zinc-300 mb-1">Destination URL</label>
          <input
            value={href}
            onChange={(e) => setHref(e.target.value)}
            placeholder="/about or https://…"
            className="w-full mb-3 px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-1 text-xs text-zinc-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={save}
              className="px-3 py-1 text-xs bg-amber-500 hover:bg-amber-400 text-black font-bold rounded"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </span>
  );
};

export default EditableLink;
